import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Switch,
  Text,
} from "@mantine/core";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle, Subnav, Can } from "@components/shared";
import { ProfileForm, ProfileOrganizations } from "@components/Profile";
import { PERMISSIONS } from "@util/permissions";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "gray";
  if (status === 3) return "red";
  return "gray";
}

export default function ProfileDetail() {
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;

  useEffect(() => {
    fetchData();
  }, [id]);

  function fetchData() {
    setError(null);
    axios
      .get(`/occupational-profiles/${id}/`)
      .then(({ data }) => {
        setProfile(data.response[0]);
      })
      .catch((err) => {
        setProfile(null);
        setError(typeof err === "string" ? err : "Unable to load profile");
        toast.error(err);
      });
  }

  function deleteProfile() {
    setBusyKey("delete");
    axios
      .delete(`/occupational-profiles/${id}/`)
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        navigate("/profiles");
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!profile) return null;

  const isDeleted = profile.status === 3;

  return (
    <div>
      <Group align="flex-start" justify="space-between" wrap="wrap">
        <PageTitle
          title={`${profile.original_name}`}
          subtitle={"Profile"}
          icon={entityIcon.profile()}
          iconColor={entityColor.profile}
        />
        {profile.status_formatted && (
          <Badge color={statusColor(profile.status)} variant="light" mt="sm">
            {profile.status_formatted}
          </Badge>
        )}
      </Group>
      <Box mt="md" mb="md">
        <Subnav
          links={[
            { text: t("detail"), to: `/profiles/${id}` },
            {
              text: t("cs:organization_plural"),
              to: `/profiles/${id}/organizations`,
            },
          ]}
        />
      </Box>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <Can permission={PERMISSIONS.OCCUPATIONAL_PROFILES_EDIT}>
                <Card mt="md">
                  <ProfileActiveToggle
                    id={id}
                    isActive={profile.status === 1}
                    isDeleted={isDeleted}
                    fetchData={fetchData}
                  />
                </Card>
              </Can>
              <Card mt="md">
                <ProfileForm id={id} profile={profile} onSuccess={fetchData} />
              </Card>
              {!isDeleted && (
                <Can permission={PERMISSIONS.OCCUPATIONAL_PROFILES_DELETE}>
                  <Card mt="md">
                    <Text size="sm" c="dimmed" mb="sm">
                      Soft-delete this profile. Org joins are removed from active
                      use; existing FPF snapshots stay frozen.
                    </Text>
                    <Button
                      color="red"
                      variant="light"
                      loading={busyKey === "delete"}
                      onClick={deleteProfile}
                    >
                      {t("delete")}
                    </Button>
                  </Card>
                </Can>
              )}
            </>
          }
        />
        <Route
          path="/organizations"
          element={
            <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_VIEW}>
              <ProfileOrganizations occupationalProfileId={id} />
            </Can>
          }
        />
      </Routes>
    </div>
  );
}

function ProfileActiveToggle({ id, isActive, isDeleted, fetchData }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function toggleActive() {
    setLoading(true);
    axios
      .post(`/occupational-profiles/${id}/active-toggle/`)
      .then(() => {
        setLoading(false);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  if (isDeleted) {
    return (
      <Switch
        label={t("activation_status")}
        checked={false}
        disabled
        description="Deleted profiles cannot be reactivated here."
      />
    );
  }

  return (
    <Switch
      label={t("activation_status")}
      checked={!!isActive}
      disabled={loading}
      onChange={() => toggleActive()}
    />
  );
}
